import { nextTick, ref } from 'vue'

import type { SearchScope } from '@/domains/command-center/services/searchService.types'
import type { SearchResultCommand } from '@/domains/command-center/types'

const injectedBarcode = ref<string | undefined>()
const injectedSearchResults = ref<SearchResultCommand[] | undefined>()
const isInjectionInFlight = ref(false)
const isBarcodeLookupPending = ref(false)

/**
 * The barcode a scan could not look up, because the Products pill was off. The palette turns it
 * into a row.
 *
 * ⚠ **The barcode, not a boolean.** A flag had no way to know which query it described, so it
 * survived the query being cleared and the row read "Switch Products on to look up """.
 * Holding the code means the palette can show the row only while that code is still the query,
 * which clears it without anything having to remember to.
 */
const scanBlockedBarcode = ref<string | undefined>()

const setScanBlockedBarcode = (barcode?: string) => {
  scanBlockedBarcode.value = barcode
}

/**
 * Whether a barcode may be looked up at all, and with which scope.
 *
 * `isAvailable` is the permission: without `catalog.view` the whole pipeline stands down — no
 * lookup, no toast, no auto-forward. `scope` is the pill: present only while Products is switched
 * on, and it carries the search function the lookup runs.
 */
interface BarcodeScopeState {
  isAvailable: boolean
  scope?: SearchScope
  /**
   * Whether a single match may open its product on the home page.
   *
   * False while the palette is in stock mode. There a scan names the item to count, and opening its
   * product would carry the merchant off the page in the middle of the count.
   */
  canNavigate: boolean
}

// The Command Center owns the pills; the scanner pipeline must obey them. It registers a reader
// here rather than the pipeline importing the palette's state — the two mount as siblings in
// AppLayout, and this module is already the channel between them.
//
// Unregistered reads as "not available", so a scan before the palette mounts is inert.
let barcodeScopeReader: (() => BarcodeScopeState) | undefined

const registerBarcodeScopeReader = (fn: () => BarcodeScopeState) => {
  barcodeScopeReader = fn
}

const readBarcodeScope = (): BarcodeScopeState => barcodeScopeReader?.() ?? { isAvailable: false, canNavigate: false }

const setBarcodeLookupPending = (pending: boolean) => {
  isBarcodeLookupPending.value = pending
}

const injectBarcode = (barcode: string, searchResults?: SearchResultCommand[]) => {
  isInjectionInFlight.value = true
  injectedBarcode.value = barcode
  injectedSearchResults.value = searchResults
  // Clear after watchers have flushed — ensures the CommandCenter reads the value before it's reset
  void nextTick(() => {
    injectedBarcode.value = undefined
    injectedSearchResults.value = undefined
    isInjectionInFlight.value = false
  })
}

// Single callback slot — only one consumer (useBarcodeAutoForward) should register.
// Subsequent calls overwrite the previous callback by design.
let consumedCallback: (() => void) | undefined

const onBarcodeConsumed = (cb: () => void) => {
  consumedCallback = cb
}

const notifyBarcodeConsumed = () => {
  consumedCallback?.()
}

export type { BarcodeScopeState }

export {
  injectedBarcode,
  scanBlockedBarcode,
  setScanBlockedBarcode,
  registerBarcodeScopeReader,
  readBarcodeScope,
  injectedSearchResults,
  isInjectionInFlight,
  isBarcodeLookupPending,
  setBarcodeLookupPending,
  injectBarcode,
  onBarcodeConsumed,
  notifyBarcodeConsumed
}
